import { useEffect, useRef, useState, useCallback } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiRequest } from '@/lib/supabase'

interface AutosaveState {
  status: 'idle' | 'saving' | 'saved' | 'error'
  lastSaved: Date | null
}

export function useBioPageAutosave(pageId: string | undefined) {
  const queryClient = useQueryClient()
  const [autoSaveState, setAutoSaveState] = useState<AutosaveState>({
    status: 'idle',
    lastSaved: null,
  })
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingDataRef = useRef<Record<string, unknown> | null>(null)

  const mutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiRequest(`/api/bio-pages/${pageId}`, 'PUT', data),
    onSuccess: () => {
      setAutoSaveState({ status: 'saved', lastSaved: new Date() })
      queryClient.invalidateQueries({ queryKey: ['bio-page', pageId] })
      // Reset to idle after 2 seconds
      setTimeout(() => setAutoSaveState(prev => prev.status === 'saved' ? { ...prev, status: 'idle' } : prev), 2000)
    },
    onError: () => {
      setAutoSaveState(prev => ({ ...prev, status: 'error' }))
    },
  })

  const save = useCallback((data: Record<string, unknown>) => {
    if (!pageId) return
    pendingDataRef.current = data

    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }

    setAutoSaveState(prev => ({ ...prev, status: 'saving' }))

    timerRef.current = setTimeout(() => {
      if (pendingDataRef.current) {
        mutation.mutate(pendingDataRef.current)
        pendingDataRef.current = null
      }
    }, 800)
  }, [pageId, mutation])

  // Flush on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
      if (pendingDataRef.current && pageId) {
        // Fire-and-forget save on unmount
        apiRequest(`/api/bio-pages/${pageId}`, 'PUT', pendingDataRef.current).catch(() => {})
      }
    }
  }, [pageId])

  return { save, ...autoSaveState }
}
